import { computed, nextTick, ref } from "vue";

import { useGetSkNwasQuery } from "gql/api";
import {
  buildNwaByIdQueryString,
  type QuoteNwaResolution
} from "@/utils/quoteHelpers";

/**
 * Look up canonical S4 NWAs by id through SK (`/api/v3/nwas/?id=`).
 *
 * Quote headers keep the NWA id; names on planning orders and old quotes are
 * often retired ECC names. ERP returns `display_name` (the AL Name when set).
 * Lookups run one at a time because the paused query shares its variables.
 */
export function useCanonicalNwaLookup() {
  const queryString = ref("");
  const { executeQuery } = useGetSkNwasQuery({
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => ({ summitQueryString: queryString.value }))
  });

  let queue: Promise<unknown> = Promise.resolve();

  const resolveNwa = (
    nwaId: string | number | null | undefined
  ): Promise<QuoteNwaResolution> => {
    const nextQueryString = buildNwaByIdQueryString(nwaId);
    if (!nextQueryString) {
      return Promise.resolve({ looked_up: false, nwa: null });
    }

    const lookup = queue.then(async (): Promise<QuoteNwaResolution> => {
      queryString.value = nextQueryString;
      await nextTick();
      try {
        const resp = await executeQuery();
        if (resp?.error?.value) {
          return { looked_up: false, nwa: null };
        }
        const rows = (resp?.data?.value?.getSKNwas || []).filter(Boolean);
        const match =
          rows.find(row => String(row?.id) === String(nwaId)) || null;
        return { looked_up: true, nwa: match };
      } catch {
        return { looked_up: false, nwa: null };
      }
    });
    queue = lookup.catch(() => undefined);
    return lookup;
  };

  return { resolveNwa };
}
